import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { conselhoCasesHref } from '@/pages/cade/ConselhoCasesPage';
import type {
  AnswerOut,
  ConselhoDocketRow,
  ConselhoMemberOutvoted,
  ConselhoRelatorOutvoted,
  ConselhoVista,
} from '@/services/types';

/** The two bench sections, each rendered only when the payload carries it
 * (the fields are CADE-only and optional on the shared model). */
export function VistaDocketSections({
  vista,
  docket,
  relatorOutvoted,
  outvoted,
}: {
  vista?: AnswerOut<ConselhoVista> | null;
  docket?: AnswerOut<ConselhoDocketRow[]> | null;
  relatorOutvoted?: AnswerOut<ConselhoRelatorOutvoted[]> | null;
  outvoted?: AnswerOut<ConselhoMemberOutvoted[]> | null;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <>
      {outvoted ? (
        <section className="rounded-lg border bg-card p-4" data-panel="outvoted">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.outvoted.title')}</h2>
          <Answer answer={outvoted} help={t('cade:conselho.outvoted.help')}>
            {(rows) => <MemberOutvotedTable rows={rows} />}
          </Answer>
        </section>
      ) : null}
      {relatorOutvoted ? (
        <section className="rounded-lg border bg-card p-4" data-panel="relator-outvoted">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.relatorOutvoted.title')}</h2>
          <Answer answer={relatorOutvoted} help={t('cade:conselho.relatorOutvoted.help')}>
            {(rows) => <RelatorOutvotedTable rows={rows} />}
          </Answer>
        </section>
      ) : null}
      {vista ? (
        <section className="rounded-lg border bg-card p-4" data-panel="vista">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.vista.title')}</h2>
          <Answer answer={vista} help={t('cade:conselho.vista.help')}>
            {(value) => <VistaPanel vista={value} />}
          </Answer>
        </section>
      ) : null}
      {docket ? (
        <section className="rounded-lg border bg-card p-4" data-panel="docket">
          <h2 className="mb-3 text-sm font-semibold text-foreground">{t('cade:conselho.docket.title')}</h2>
          <Answer answer={docket} help={t('cade:conselho.docket.help')}>
            {(rows) => <DocketTable rows={rows} />}
          </Answer>
        </section>
      ) : null}
    </>
  );
}

function useFormats() {
  const { i18n } = useTranslation();
  return {
    number: new Intl.NumberFormat(i18n.language),
    percent: new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 0 }),
    ratio: new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  };
}

/**
 * Pedidos de vista: who asked, and what the vista cases ended as beside every
 * case with a final decision. Counts and Wilson intervals; the minutes'
 * unanimity flag is shown with the measured count of items whose own ruling
 * text contradicts it, so the share is read with its caveat attached.
 */
export function VistaPanel({ vista }: { vista: ConselhoVista }) {
  const { t } = useTranslation(['cade']);
  const { number, percent } = useFormats();
  const f = vista.follow;
  const shareWithInterval = (share: number | null, lower: number | null, upper: number | null) =>
    share === null ? '—' : `${percent.format(share)} [${percent.format(lower ?? 0)}, ${percent.format(upper ?? 0)}]`;
  return (
    <div className="space-y-3">
      <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2" data-testid="vista-follow">
        <div>
          <dt className="text-muted-foreground">{t('cade:conselho.vista.nonUnanimousVista', { resolved: f.resolved, cases: f.vista_cases })}</dt>
          <dd className="tabular-nums text-foreground" data-testid="vista-share">
            {number.format(f.non_unanimous)} / {number.format(f.resolved)} ·{' '}
            {shareWithInterval(vista.non_unanimous_share, vista.non_unanimous_lower, vista.non_unanimous_upper)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('cade:conselho.vista.nonUnanimousBaseline')}</dt>
          <dd className="tabular-nums text-foreground" data-testid="vista-baseline">
            {number.format(f.baseline_non_unanimous)} / {number.format(f.baseline_final)} ·{' '}
            {shareWithInterval(vista.baseline_share, vista.baseline_lower, vista.baseline_upper)}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-muted-foreground">{t('cade:conselho.vista.fines', { n: f.with_proposed_fine })}</dt>
          <dd className="tabular-nums text-foreground" data-testid="vista-fines">
            {t('cade:conselho.vista.fineBreakdown', {
              absent: f.fine_absent,
              raised: f.fine_raised,
              lowered: f.fine_lowered,
              same: f.fine_same,
            })}
          </dd>
        </div>
      </dl>
      <p className="text-[11px] text-muted-foreground" data-testid="vista-unanimity">
        {t('cade:conselho.vista.unanimityCaveat', {
          outvoted: vista.unanimity.text_names_outvoted,
          flagged: vista.unanimity.flagged_unanimous,
        })}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="vista-requesters">
          <thead className="sticky top-0 z-10 bg-muted text-left">
            <tr>
              <th className="p-2">{t('cade:conselho.minutes.member')}</th>
              <th className="p-2 text-right">{t('cade:conselho.vista.requests')}</th>
            </tr>
          </thead>
          <tbody>
            {vista.requesters.map((r) => (
              <tr key={`${r.slug}|${r.resolved}`} className={`border-t ${r.resolved ? '' : 'text-muted-foreground'}`} data-resolved={r.resolved ? 'true' : 'false'}>
                <td className="p-2">
                  {r.resolved ? (
                    <Link to={`/cade/r/${r.slug}`} className="underline-offset-4 hover:underline">
                      {r.name}
                    </Link>
                  ) : (
                    <>
                      {r.name} <span className="text-[11px]">({t('cade:conselho.vista.unresolved')})</span>
                    </>
                  )}
                </td>
                <td className="p-2 text-right tabular-nums">{number.format(r.decisions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {vista.unresolved_decisions > 0 ? (
        <p className="text-[11px] text-muted-foreground" data-testid="vista-unresolved">
          {t('cade:conselho.vista.unresolvedNote', { count: vista.unresolved_decisions })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * How often each member ended on the losing side of a complete item, against
 * what the all-member rate of their own years predicts. Listed by name; the
 * items count opens the list behind it. ``linked`` is off on a member's own
 * page, where the name would link to itself.
 */
export function MemberOutvotedTable({ rows, linked = true }: { rows: ConselhoMemberOutvoted[]; linked?: boolean }) {
  const { t } = useTranslation(['cade']);
  const { number, percent, ratio } = useFormats();
  return (
    <div className="max-h-[28rem] overflow-auto">
      <table className="w-full text-xs" data-testid="outvoted-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.minutes.member')}</th>
            <th className="p-2">{t('cade:conselho.relatorOutvoted.years')}</th>
            <th className="p-2 text-right">{t('cade:conselho.outvoted.items')}</th>
            <th className="p-2 text-right">{t('cade:conselho.outvoted.outvoted')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.expected')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.slug} className={`border-t ${r.suppressed ? 'text-muted-foreground' : ''}`} data-suppressed={r.suppressed ? 'true' : 'false'}>
              <td className="p-2">
                {linked ? (
                  <Link to={`/cade/r/${r.slug}`} className="underline-offset-4 hover:underline">
                    {r.name}
                  </Link>
                ) : (
                  r.name
                )}
              </td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {r.first_year === r.last_year ? r.first_year : `${r.first_year}–${r.last_year}`}
              </td>
              <td className="p-2 text-right tabular-nums">
                <Link
                  to={conselhoCasesHref('outvoted', r.name)}
                  className="underline-offset-4 hover:underline"
                  data-testid="outvoted-items-link"
                >
                  {number.format(r.items)}
                </Link>
              </td>
              <td className="p-2 text-right tabular-nums" data-testid="outvoted-share">
                {number.format(r.outvoted)}
                {r.outvoted_share !== null && r.outvoted_lower !== null && r.outvoted_upper !== null ? (
                  <>
                    {' '}
                    <span className="text-muted-foreground">
                      {percent.format(r.outvoted_share)} [{percent.format(r.outvoted_lower)}, {percent.format(r.outvoted_upper)}]
                    </span>
                  </>
                ) : null}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.expected_outvoted)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="outvoted-ratio">
                {r.observed_over_expected !== null ? ratio.format(r.observed_over_expected) : '—'}
                {r.suppressed ? ` (${t('cade:conselho.pairs.belowFloor')})` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * How often the plenary outvoted each relator, from the ruling text, against
 * what the rate of every relator in the same years predicts. Listed by name;
 * the observed/expected ratio with the interval on the share is the reading.
 */
export function RelatorOutvotedTable({ rows, linked = true }: { rows: ConselhoRelatorOutvoted[]; linked?: boolean }) {
  const { t } = useTranslation(['cade']);
  const { number, percent, ratio } = useFormats();
  return (
    <div className="max-h-[28rem] overflow-auto">
      <table className="w-full text-xs" data-testid="relator-outvoted-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.docket.relator')}</th>
            <th className="p-2">{t('cade:conselho.relatorOutvoted.years')}</th>
            <th className="p-2 text-right">{t('cade:conselho.relatorOutvoted.decided')}</th>
            <th className="p-2 text-right">{t('cade:conselho.relatorOutvoted.outvoted')}</th>
            <th className="p-2 text-right">{t('cade:conselho.relatorOutvoted.outright')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.expected')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.ratio')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.slug} className={`border-t ${r.suppressed ? 'text-muted-foreground' : ''}`} data-suppressed={r.suppressed ? 'true' : 'false'}>
              <td className="p-2">
                {linked ? (
                  <Link to={`/cade/r/${r.slug}`} className="underline-offset-4 hover:underline">
                    {r.name}
                  </Link>
                ) : (
                  r.name
                )}
              </td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {r.first_year === r.last_year ? r.first_year : `${r.first_year}–${r.last_year}`}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.decided)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="relator-outvoted-share">
                {number.format(r.outvoted)}
                {r.outvoted_share !== null && r.outvoted_lower !== null && r.outvoted_upper !== null ? (
                  <>
                    {' '}
                    <span className="text-muted-foreground">
                      {percent.format(r.outvoted_share)} [{percent.format(r.outvoted_lower)}, {percent.format(r.outvoted_upper)}]
                    </span>
                  </>
                ) : null}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.outright)}</td>
              <td className="p-2 text-right tabular-nums">{number.format(r.expected_outvoted)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="relator-outvoted-ratio">
                {r.observed_over_expected !== null ? ratio.format(r.observed_over_expected) : '—'}
                {r.suppressed ? ` (${t('cade:conselho.pairs.belowFloor')})` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The 1997-2026 pauta docket per relator: carry-over to a later pauta against
 * what the corpus rate of the relator's own assignment years predicts. Listed
 * by name; the observed-over-expected ratio and its interval are the reading,
 * never a rank.
 */
export function DocketTable({ rows }: { rows: ConselhoDocketRow[] }) {
  const { t } = useTranslation(['cade']);
  const { number, percent, ratio } = useFormats();
  return (
    <div className="max-h-[28rem] overflow-auto">
      <table className="w-full text-xs" data-testid="docket-table">
        <thead className="sticky top-0 z-10 bg-muted text-left">
          <tr>
            <th className="p-2">{t('cade:conselho.docket.relator')}</th>
            <th className="p-2">{t('cade:conselho.docket.years')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.processes')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.carried')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.expected')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.ratio')}</th>
            <th className="p-2 text-right">{t('cade:conselho.docket.redistributed')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.slug} className={`border-t ${r.suppressed ? 'text-muted-foreground' : ''}`} data-suppressed={r.suppressed ? 'true' : 'false'}>
              <td className="p-2">{r.name}</td>
              <td className="p-2 tabular-nums text-muted-foreground">
                {r.first_year === r.last_year ? r.first_year : `${r.first_year}–${r.last_year}`}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.processes)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="docket-carried">
                {number.format(r.carried)}
                {r.carried_share !== null && r.carried_lower !== null && r.carried_upper !== null ? (
                  <>
                    {' '}
                    <span className="text-muted-foreground">
                      {percent.format(r.carried_share)} [{percent.format(r.carried_lower)}, {percent.format(r.carried_upper)}]
                    </span>
                  </>
                ) : null}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.expected_carried)}</td>
              <td className="p-2 text-right tabular-nums" data-testid="docket-ratio">
                {r.observed_over_expected !== null ? ratio.format(r.observed_over_expected) : '—'}
                {r.suppressed ? ` (${t('cade:conselho.pairs.belowFloor')})` : ''}
              </td>
              <td className="p-2 text-right tabular-nums">{number.format(r.redistributed)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
